import React from 'react';
import { css, styled } from '@this/constants/themes';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import SelectTransportBoxPriceRangeLabel from '../price_range_label';

type Props = {
  loading: boolean;
  selected: boolean;
  hovered: boolean;
  price?: number;
  isAdditional?: boolean;
  priceRange?: string;
  showsPricePerPersonText?: boolean;
  changeable?: boolean;
  onClick: () => void;
  onTouchStart: () => void;
  onMouseOver: () => void;
  onMouseLeave: () => void;
  seatType?: string | null;
  isPackage?: true | null;
  seatCount?: string | null;
  isDomestic?: boolean;
  disabled?: boolean;
};

const SelectTransportBoxRightInListTemplate: React.FC<Props> = ({
  loading,
  selected,
  hovered,
  price,
  isAdditional,
  priceRange,
  showsPricePerPersonText,
  changeable,
  onClick,
  onTouchStart,
  onMouseOver,
  onMouseLeave,
  isPackage,
  seatCount,
  isDomestic,
  disabled
}) => (
  <TransportSelectButton
    selected={selected || hovered}
    onClick={onClick}
    onTouchStart={onTouchStart}
    onMouseOver={onMouseOver}
    onMouseLeave={onMouseLeave}
    isPackage={isPackage}
    isDomestic={isDomestic}
    disabled={disabled}
  >
    {loading ? (
      <RightBody>
        <Loading />
      </RightBody>
    ) : hovered && !selected ? (
      <div className="select-transport-box__right__body__select">選択する</div>
    ) : (
      <RightBox className="select-transport-box__right__body">
        {changeable != null && (
          <Changeable selected={selected}>
            <RightChangeable className="select-transport-box__right__body__changeable flex">
              変更
              <br />
              {changeable ? '可' : '不可'}
            </RightChangeable>
          </Changeable>
        )}
        <Details selected={selected}>
          <div>
            <RightPrice>
              {price !== undefined && (
                <>
                  {isAdditional && <>+ </>}
                  <span data-wovn-ignore>{utils.digits(price)}</span>
                  <Yen>円</Yen>
                </>
              )}
              {priceRange && <SelectTransportBoxPriceRangeLabel priceRange={priceRange} />}
            </RightPrice>
            <RightPeopleNum>{showsPricePerPersonText ? '/1名' : ''}</RightPeopleNum>
          </div>
          {seatCount != null && <div>{seatCount}</div>}
        </Details>
      </RightBox>
    )}
  </TransportSelectButton>
);

SelectTransportBoxRightInListTemplate.defaultProps = {
  showsPricePerPersonText: true
};

/**
 * この書き方でないとCannot create styled-component for component: undefinedになる
 * ref: https://github.com/styled-components/styled-components/issues/1449#issuecomment-420087359
 */
const TransportSelectButton = styled(props => <RightButton {...props} />)<
  Pick<Props, 'isPackage' | 'disabled' | 'isDomestic'>
>`
  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.6;
          pointer-events: none;
        `
      : null}

  ${({ isPackage }) =>
    isPackage
      ? css`
          min-width: 145px;
          min-height: 45px;
          text-align: center;
        `
      : null}

  ${({ isDomestic }) =>
    isDomestic
      ? css`
          height: 3rem;
        `
      : null}
  padding: 0;
  width: 100%;
`;

const RightBox = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const Changeable = styled.div<Pick<Props, 'selected'>>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 100%;
  background: ${props => (props.selected ? props.theme.accentColor : 'inherit')};
`;

const Details = styled.div<Pick<Props, 'selected'>>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: ${props => (props.selected ? props.theme.accentColor : 'inherit')};
`;

const RightButton = styled.div<{ selected: boolean; isPackage?: true | null; isDomestic?: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  border: 2px solid ${props => props.theme.linkColor};
  border-radius: 3px;
  padding: 2px;
  margin: 2px;
  color: ${props => props.theme.linkColor};
  font-weight: bold;
  cursor: pointer;
  min-width: 87px;
  height: ${props => (props.isDomestic ? '3rem' : '70px')};
  &.flight {
    min-width: 116px;
  }
  &.disabled {
    border-color: ${props => props.theme.grayBorderColor};
    color: ${props => props.theme.grayTextColor};
  }
  ${props => props.selected && `background: ${props.theme.linkColor}; color: white;`}
  ${props => props.isPackage && `min-width: 145px; min-height: 45px; text-align: center;`}
`;

const RightBody = styled.div`
  text-align: center;
  margin-left: auto;
  margin-right: auto;
`;

const RightChangeable = styled.div`
  font-size: 11px;
`;

const RightPrice = styled.span`
  font-size: 14px;
`;

const RightPeopleNum = styled.span`
  font-size: 11px;
`;

const Yen = styled.span`
  font-size: 11px;
`;

export default SelectTransportBoxRightInListTemplate;
